import React, { ChangeEventHandler, FC, useState } from 'react';
import styled from 'styled-components';

import TextInput, { IStyledTextInputProps } from '@ui/forms/TextInput';

import CrossedEye from '@assets/vectors/crossed-eye.inline.svg';
import OpenEye from '@assets/vectors/open-eye.inline.svg';

type FormatterProps =
	| {
			changeHandler?: undefined;
			cleanValue?: never;
			formatter?: never;
			masker?: undefined;
	  }
	| {
			changeHandler: (iVal: string, rawVal: string) => string;
			cleanValue?: (value: string) => string;
			formatter?: (value: string) => string;
			masker: (raw: string) => string;
	  };

export interface ISecureInputProps {
	label?: string;
	subLabel?: string;
	name?: string;
	onChange?: (...event: any[]) => void;
	value?: string;
	error?: boolean;
	errorLabel?: string;
	isPassword?: boolean;
	className?: string;
	inputType?: string;
}

const BlankButton = styled.button`
	position: absolute;
	border: 0;
	background: none;
	margin: 0;
	padding: 0;
	right: 1rem;
	top: 30%;
	height: 2.5rem;
	width: 2.5rem;
	display: flex;
	align-items: center;
	justify-content: center;
`;

const Wrapper = styled.div`
	display: flex;
	position: relative;
`;

const SecureInput: FC<ISecureInputProps & FormatterProps & IStyledTextInputProps> = ({
	label,
	name = 'textInput',
	changeHandler,
	cleanValue = (val) => val,
	formatter = (val) => val,
	masker,
	onChange = (val) => {},
	value = '',
	isPassword = false,
	inputType = '',
	...splatProps
}) => {
	const [rawValue, setRawValue] = useState(cleanValue(value));
	const [showRaw, setShowRaw] = useState(false);

	const mask = (val: string) => {
		const masked = masker ? masker(val) : val.replace(/[a-zA-Z0-9]/g, '•');

		return masked;
	};

	const getRawValue = (value: string, rawVal: string): string => {
		const lastChar = value.slice(-1);

		if (lastChar.match(/[^a-zA-Z0-9]/g)) {
			return rawVal;
		}

		return rawVal + lastChar;
	};

	const handleChange: ChangeEventHandler<HTMLInputElement> = (event) => {
		const { value } = event.target;
		const cleanedValue = cleanValue(value);

		if (value.match(/^$/)) {
			const emptyValue = '';
			setRawValue(emptyValue);
			onChange(emptyValue);
			return;
		}

		if (!isPassword && cleanedValue.length < rawValue.length) {
			const last = rawValue.length - 1;
			const newValue = rawValue.slice(0, last);
			setRawValue(newValue);
			onChange(newValue);
			return;
		}

		const newRaw = changeHandler
			? changeHandler(cleanedValue, rawValue)
			: getRawValue(cleanedValue, rawValue);

		setRawValue(newRaw);
		onChange(formatter(newRaw));
	};

	const blankButton = (
		<BlankButton
			aria-label={showRaw ? `${inputType} visible` : `${inputType} hidden`}
			type="button"
			onClick={() => setShowRaw(!showRaw)}>
			{showRaw ? <OpenEye alt="open eye" /> : <CrossedEye alt="crossed out eye" />}
		</BlankButton>
	);

	return (
		<Wrapper>
			<input type="hidden" value={formatter(rawValue)} name={name} />
			{showRaw ? (
				<TextInput
					label={label}
					name={`raw-${name}`}
					value={formatter(rawValue)}
					onChange={handleChange}
					fullWidth
					{...splatProps}>
					{blankButton}
				</TextInput>
			) : (
				<TextInput
					type={isPassword ? 'password' : 'text'}
					label={label}
					name={`secure-${name}`}
					value={mask(formatter(rawValue))}
					onChange={handleChange}
					fullWidth
					{...splatProps}>
					{blankButton}
				</TextInput>
			)}
		</Wrapper>
	);
};

export default styled(SecureInput)``;
